import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ChevronLeft, ChevronRight, MapPin, FileText, Clock, Edit2, Trash2, Download, Users } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import ConfirmModal from '../components/ConfirmModal';
import { SkeletonAgendaItem } from '../components/Skeleton';
import { toDateStr, formatTanggalID, dateToStr, todayStr } from '../utils/dateUtils';

const HARI = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
const BULAN_NAMA = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];

const formatJam = (j) => j ? j.slice(0, 5) : '--:--';

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const days = [];
  for (let i = firstDay - 1; i >= 0; i--)
    days.push({ date: new Date(year, month - 1, daysInPrev - i), current: false });
  for (let d = 1; d <= daysInMonth; d++)
    days.push({ date: new Date(year, month, d), current: true });
  const remaining = 42 - days.length;
  for (let d = 1; d <= remaining; d++)
    days.push({ date: new Date(year, month + 1, d), current: false });
  return days;
}

export default function LihatJadwalPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  // Gunakan dateToStr(new Date()) — local time, bukan UTC
  const todayYMD = todayStr();
  const todayDate = new Date(); // hanya untuk month/year init

  const [viewYear, setViewYear] = useState(todayDate.getFullYear());
  const [viewMonth, setViewMonth] = useState(todayDate.getMonth());
  const [selectedDate, setSelectedDate] = useState(todayYMD);
  const [eventDates, setEventDates] = useState(new Set());
  const [agenda, setAgenda] = useState([]);
  const [loadingAgenda, setLoadingAgenda] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const calendarDays = buildCalendarDays(viewYear, viewMonth);

  const fetchEventDates = useCallback(() => {
    api.get('/jadwal/dates-with-events', { params: { bulan: viewMonth + 1, tahun: viewYear } })
      .then(res => {
        // Backend sekarang return string "YYYY-MM-DD" langsung (dateStrings config)
        // Pastikan hanya ambil 10 karakter pertama
        const dates = res.data.data.map(d => toDateStr(d));
        setEventDates(new Set(dates));
      })
      .catch(console.error);
  }, [viewMonth, viewYear]);

  useEffect(() => { fetchEventDates(); }, [fetchEventDates]);

  useEffect(() => {
    document.title = "Jadwal - Penjadwalan Acara Pimpinan";
    if (!selectedDate) return;
    setLoadingAgenda(true);
    api.get(`/jadwal/date/${selectedDate}`)
      .then(res => setAgenda(res.data.data))
      .catch(console.error)
      .finally(() => setLoadingAgenda(false));
  }, [selectedDate]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };
  const goToday = () => {
    setViewMonth(todayDate.getMonth());
    setViewYear(todayDate.getFullYear());
    setSelectedDate(todayYMD);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/jadwal/${deleteTarget.id}`);
      toast.success('Jadwal berhasil dihapus');
      setAgenda(prev => prev.filter(j => j.id !== deleteTarget.id));
      fetchEventDates();
      setDeleteTarget(null);
    } catch { toast.error('Gagal menghapus jadwal'); }
    finally { setDeleting(false); }
  };

  const handleDownloadLampiran = async (jadwal) => {
    try {
      const res = await api.get(`/jadwal/${jadwal.id}/lampiran`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = jadwal.lampiran; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Gagal mengunduh lampiran'); }
  };

  // Format label tanggal terpilih — gunakan helper bukan new Date() langsung
  const selectedLabel = selectedDate
    ? formatTanggalID(selectedDate, { weekday: 'long' })
    : '';

  return (
    <div className="page-container">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 24 }} onClick={() => navigate('/')}>
        <ChevronLeft size={16} /> Kembali
      </button>

      <div className="page-header">
        <h1 className="page-title">Lihat Jadwal Pimpinan</h1>
        <p className="page-subtitle">Kalender jadwal kegiatan pimpinan</p>
      </div>

      <div className="calendar-layout">
        {/* Kalender */}
        <div className="card">
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, gap: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={prevMonth}><ChevronLeft size={18} /></button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ fontSize: 18, fontWeight: 700 }}>{BULAN_NAMA[viewMonth]} {viewYear}</h2>
                <button className="btn btn-outline btn-sm" onClick={goToday} style={{ fontSize: 12 }}>
                  Hari Ini
                </button>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={nextMonth}><ChevronRight size={18} /></button>
            </div>

            {/* Header hari */}
            <div className="calendar-grid" style={{ marginBottom: 8 }}>
              {HARI.map(h => (
                <div key={h} style={{ textAlign: 'center', fontSize: 12, fontWeight: 700, color: 'var(--gray-400)', padding: '4px 0' }}>
                  {h}
                </div>
              ))}
            </div>

            {/* Grid tanggal */}
            <div className="calendar-grid">
              {calendarDays.map((item, i) => {
                // dateToStr pakai local time — ini kunci fix-nya
                const ymd = dateToStr(item.date);
                const isToday = ymd === todayYMD;
                const isSelected = ymd === selectedDate;
                const hasEvent = eventDates.has(ymd);
                return (
                  <div key={i}
                    className={[
                      'calendar-day',
                      isSelected ? 'selected' : '',
                      isToday && !isSelected ? 'today' : '',
                      !item.current ? 'other-month' : '',
                      hasEvent ? 'has-event' : '',
                    ].join(' ')}
                    onClick={() => setSelectedDate(ymd)}
                    style={{ padding: '8px 0' }}
                  >
                    {item.date.getDate()}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Panel agenda */}
        <div className="card agenda-panel">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, background: 'var(--primary-bg)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="var(--primary)" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>Agenda</div>
                <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{selectedLabel}</div>
              </div>
            </div>
            {isAdmin && (
              <button className="btn btn-primary btn-sm"
                onClick={() => navigate(`/input?tanggal=${selectedDate}`)}>
                + Tambah
              </button>
            )}
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, maxHeight: 520, overflowY: 'auto' }}>
            {loadingAgenda ? (
              Array(3).fill(0).map((_, i) => <SkeletonAgendaItem key={i} />)
            ) : agenda.length === 0 ? (
              <div className="empty-state" style={{ padding: '40px 20px' }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>📅</div>
                <p style={{ fontSize: 14 }}>Tidak ada agenda pada tanggal ini</p>
              </div>
            ) : (
              agenda.map(j => (
                <div key={j.id} className="agenda-item">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', background: 'var(--primary-bg)', color: 'var(--primary)', borderRadius: 100, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Clock size={10} /> {formatJam(j.jam_mulai)} – {formatJam(j.jam_selesai)}
                    </span>
                    {j.disposisi == 1 && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', background: 'var(--orange)', color: 'var(--gray-900)', borderRadius: 100, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        Disposisi
                        </span>
                      )}
                    <div style={{ display: 'flex', gap: 2 }}>
                      {isAdmin && (
                        <button className="btn btn-ghost btn-sm" style={{ padding: '4px 7px' }}
                          onClick={() => navigate(`/input?edit=${j.id}`)} title="Edit">
                          <Edit2 size={13} />
                        </button>
                      )}
                      {j.lampiran && (
                        <button className="btn btn-ghost btn-sm" style={{ padding: '4px 7px', color: 'var(--primary)' }}
                          onClick={() => handleDownloadLampiran(j)} title="Unduh Lampiran">
                          <Download size={13} />
                        </button>
                      )}
                      {isAdmin && (
                        <button className="btn btn-ghost btn-sm" style={{ padding: '4px 7px', color: 'var(--red)' }}
                          onClick={() => setDeleteTarget(j)} title="Hapus">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{ fontWeight: 700, fontSize: 15, marginTop: 8 }}>{j.nama_acara}</div>
                  {j.penyelenggara && <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>{j.penyelenggara}</div>}

                  {j.pimpinan?.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {j.pimpinan.map(p => (
                        <span key={p.id} className="badge badge-blue" style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 3 }}>
                          <Users size={10} /> {p.nama}
                        </span>
                      ))}
                    </div>
                  )}

                  {j.lokasi && (
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <MapPin size={11} /> {j.lokasi}
                    </div>
                  )}
                  {j.no_surat && (
                    <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <FileText size={11} /> {j.no_surat}
                    </div>
                  )}
                  {j.keterangan && (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 8, padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 6, lineHeight: 1.5 }}>
                      {j.keterangan}
                    </div>
                  )}
                  {j.lampiran && (
                    <button onClick={() => handleDownloadLampiran(j)}
                      style={{ marginTop: 8, fontSize: 12, color: 'var(--primary)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, padding: 0, fontFamily: 'inherit' }}>
                      <Download size={12} /> Unduh Lampiran
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        show={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Hapus Jadwal?"
        message={`Jadwal "${deleteTarget?.nama_acara}" akan dipindahkan ke arsip.`}
        confirmLabel="Ya, Hapus"
      />
    </div>
  );
}
